import React, { useState } from 'react';
import { IconMessageSquare, IconShield, IconHelp, IconBook } from '../components/Icons';

const SUPPORT_EMAIL = 'info@cultivatedynamics.com';

function ContactPage({ onNavigate }) {
  const go = (page) => () => onNavigate && onNavigate(page);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setStatus(null);

    if (!form.email || !form.message) {
      setStatus({ kind: 'err', text: 'Email and message are required.' });
      return;
    }

    setSubmitting(true);
    try {
      const subject = encodeURIComponent(
        form.subject ? `[RiskLo] ${form.subject}` : '[RiskLo] Contact form'
      );
      const body = encodeURIComponent(
        `From: ${form.name || '(no name)'} <${form.email}>\n\n${form.message}`
      );
      window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
      setStatus({ kind: 'ok', text: 'Opening your email client…' });
    } catch (err) {
      setStatus({ kind: 'err', text: 'Could not open your email client. Email us directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="marketing-page-header">
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-eyebrow marketing-reveal">Contact</div>
          <h1 className="marketing-title marketing-reveal" data-delay="1">
            Questions about RiskLo? <span className="accent">We're here.</span>
          </h1>
          <p className="marketing-subtitle marketing-reveal" data-delay="2" style={{ margin: '1rem auto 0' }}>
            Reach the team for support, account, billing, or partnership questions.
            We typically respond within a business day.
          </p>
        </div>
      </section>

      <section className="marketing-section" style={{ paddingTop: 0 }}>
        <div className="marketing-container">
          <div className="marketing-contact-grid">
            <div className="marketing-contact-card marketing-reveal">
              <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <IconMessageSquare size={18} /> Send us a message
              </h4>
              <p>
                Tell us what's going on. The form opens your email client pre-filled
                so you can review and send.
              </p>
              <form className="marketing-form" onSubmit={onSubmit}>
                <div className="marketing-form-row">
                  <div>
                    <label htmlFor="m-name">Name</label>
                    <input
                      id="m-name"
                      className="marketing-input"
                      type="text"
                      value={form.name}
                      onChange={onChange('name')}
                      placeholder="Your name"
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label htmlFor="m-email">Email *</label>
                    <input
                      id="m-email"
                      className="marketing-input"
                      type="email"
                      value={form.email}
                      onChange={onChange('email')}
                      placeholder="you@email.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                  <label htmlFor="m-subject">Subject</label>
                  <input
                    id="m-subject"
                    className="marketing-input"
                    type="text"
                    value={form.subject}
                    onChange={onChange('subject')}
                    placeholder="e.g. Billing question"
                  />
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                  <label htmlFor="m-message">Message *</label>
                  <textarea
                    id="m-message"
                    className="marketing-textarea"
                    value={form.message}
                    onChange={onChange('message')}
                    placeholder="What can we help with?"
                    required
                  />
                </div>
                <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <button type="submit" className="marketing-btn" disabled={submitting}>
                    {submitting ? 'Opening…' : 'Send via email'}
                  </button>
                  <a className="marketing-btn-link" href={`mailto:${SUPPORT_EMAIL}`}>
                    or email directly
                  </a>
                </div>
                {status && (
                  <div className={`marketing-form-status ${status.kind}`}>{status.text}</div>
                )}
              </form>
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div className="marketing-contact-card marketing-reveal" data-delay="1">
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconShield size={18} /> Direct support
                </h4>
                <p>
                  For account, billing, and security questions, email us directly.
                </p>
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </div>

              <div className="marketing-contact-card marketing-reveal" data-delay="2">
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconHelp size={18} /> Quick answers
                </h4>
                <p>Check the FAQ before emailing — most questions are answered there.</p>
                <button type="button" className="marketing-btn-link" onClick={go('help')} style={{ padding: 0 }}>
                  Read FAQ →
                </button>
              </div>

              <div className="marketing-contact-card marketing-reveal" data-delay="3">
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconBook size={18} /> Learn how RiskLo works
                </h4>
                <p>Methodology, calculations, and consistency-rule details are documented in product.</p>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button type="button" className="marketing-btn-link" onClick={go('how-we-calculate')} style={{ padding: 0 }}>
                    How we calculate →
                  </button>
                  <button type="button" className="marketing-btn-link" onClick={go('consistency-rules')} style={{ padding: 0 }}>
                    Consistency rules →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-cta marketing-reveal">
            <div className="marketing-cta-inner">
              <h2>Want a guided walkthrough?</h2>
              <p>Sign in and we'll point you at the right tool for your account setup.</p>
              <div className="marketing-hero-cta-row" style={{ marginTop: 0 }}>
                <button type="button" className="marketing-btn marketing-btn-lg" onClick={go('login')}>
                  Sign in
                </button>
                <button type="button" className="marketing-btn-ghost marketing-btn-lg" onClick={go('features')}>
                  Browse features
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default ContactPage;
